import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Filter as FilterIcon, Plus, Users } from 'lucide-react'

import BasePage from './basePage'
import { getCustomers } from '../services/customers'
import { formatCpf, formatMoney } from '../services/functions'
import { useMenu } from '../store/global/useMenu'

import { AbsenceBadge } from '../components/ui/AbsenceBadge'
import { Filter } from '../components/ui/Filter'
import { CAMPO } from '../components/ui/Filter/sizes'
import { Input } from '../components/ui/Form/Input'
import { Table } from '../components/ui/Table'

const FILTRO_VAZIO = { cpf: '', cellphone: '', name: '', pet: '' }

/** Rotulo da coluna -> campo do registro, para a ordenacao. */
const CHAVES_DE_ORDENACAO = {
  Nome: 'name',
  CPF: 'cpf',
  Telefone: 'telephone',
  Pets: 'petsCount',
  'Ticket médio': 'averageTicket',
  Ausência: 'absenceDays',
  'E-mail': 'email',
}

const CAMPOS_NUMERICOS = ['petsCount', 'averageTicket', 'absenceDays']

/**
 * Ordena pelo campo pedido. Numero compara como numero (senao "9" fica depois
 * de "10") e quem nao tem valor vai para o fim, nos dois sentidos — uma
 * cliente sem agendamento nenhum nao deve ocupar o topo de "maior ticket".
 */
function ordena(lista, campo, sentido) {
  if (!campo) return lista

  const direcao = sentido === 'desc' ? -1 : 1

  return [...lista].sort((a, b) => {
    const esquerda = a[campo]
    const direita = b[campo]

    const esquerdaVazia = esquerda === null || esquerda === undefined || esquerda === ''
    const direitaVazia = direita === null || direita === undefined || direita === ''
    if (esquerdaVazia && direitaVazia) return 0
    if (esquerdaVazia) return 1
    if (direitaVazia) return -1

    if (CAMPOS_NUMERICOS.includes(campo)) {
      return ((Number(esquerda) || 0) - (Number(direita) || 0)) * direcao
    }

    return String(esquerda).localeCompare(String(direita), 'pt-BR') * direcao
  })
}

function Clientes() {
  const history = useHistory()
  const menuSize = useMenu((st) => st.data.size)

  const [filtro, setFiltro] = useState(FILTRO_VAZIO)
  const [clientes, setClientes] = useState([])
  const [carregando, setCarregando] = useState(false)
  const [aviso, setAviso] = useState('')
  const [ordem, setOrdem] = useState({ sortBy: null, sortOrder: 'asc' })

  const altera = (campo) => setFiltro((atual) => ({ ...atual, ...campo }))

  const buscar = async (evento) => {
    if (evento?.preventDefault) evento.preventDefault()

    setCarregando(true)
    setAviso('')

    const resultado = await getCustomers(filtro.cpf, filtro.name, filtro.pet, filtro.cellphone)

    if (!Array.isArray(resultado)) {
      setClientes([])
      setAviso(resultado || 'Não foi possível buscar agora. Tente de novo.')
    } else {
      setClientes(resultado)
      if (!resultado.length) setAviso('Nenhum cliente encontrado com esses dados.')
    }

    setCarregando(false)
  }

  const limpar = () => {
    setFiltro(FILTRO_VAZIO)
    setClientes([])
    setAviso('')
    setOrdem({ sortBy: null, sortOrder: 'asc' })
  }

  const ordenaPorColuna = (campo) =>
    setOrdem((atual) => ({
      sortBy: campo,
      sortOrder: atual.sortBy === campo && atual.sortOrder === 'asc' ? 'desc' : 'asc',
    }))

  const listaOrdenada = useMemo(
    () => ordena(clientes, ordem.sortBy, ordem.sortOrder),
    [clientes, ordem]
  )

  return (
    <BasePage pageTitle={<><Users className="w-7 h-7" />Clientes</>}>
      <motion.div
        initial={{ width: `calc(100vw - 2rem - ${menuSize})` }}
        animate={{ width: `calc(100vw - 2rem - ${menuSize})` }}
        transition={{ duration: 0.3 }}
        className="pr-4"
      >
        <Filter.Root onSubmit={buscar}>
          <Filter.Container>
            <Input.Root
              label="CPF"
              mask="999.999.999-99"
              style={CAMPO.medio}
              value={filtro.cpf}
              onChange={(cpf) => altera({ cpf })}
            />
            <Input.Root
              label="Telefone"
              mask="(99) 99999-9999"
              style={CAMPO.medio}
              value={filtro.cellphone}
              onChange={(cellphone) => altera({ cellphone })}
            />
            <Input.Root
              label="Nome"
              style={CAMPO.grande}
              value={filtro.name}
              onChange={(name) => altera({ name })}
            />
            <Input.Root
              label="Pet"
              style={CAMPO.grande}
              value={filtro.pet}
              onChange={(pet) => altera({ pet })}
            />
          </Filter.Container>

          <Filter.Actions>
            <Filter.Action isLoading={carregando}>
              <Filter.ActionIcon icon={FilterIcon} isLoading={carregando} />
              Filtrar
            </Filter.Action>

            <Filter.Action
              outline
              type="button"
              name="clear-clientes"
              disabled={carregando}
              onClick={limpar}
            >
              Limpar
            </Filter.Action>

            <Filter.Action
              outline
              type="button"
              name="novo-cliente"
              onClick={() => history.push('/clientes/novo')}
            >
              <Filter.ActionIcon icon={Plus} />
              Novo cliente
            </Filter.Action>
          </Filter.Actions>
        </Filter.Root>

        <Table.Root name="clientes" config>
          <Table.Header
            columns={[
              'Nome:minmax(150px, 260px)',
              'CPF:140px',
              'Telefone:130px',
              'Pets:70px',
              'Ticket médio:110px',
              'Ausência:100px',
              'E-mail:minmax(160px, 240px)',
            ]}
            sortableConfig={{
              sortBy: ordem.sortBy,
              sortOrder: ordem.sortOrder,
              onSort: ordenaPorColuna,
              columnKeys: CHAVES_DE_ORDENACAO,
            }}
          />

          <Table.Content isLoading={carregando}>
            {listaOrdenada.map((cliente) => (
              <Table.Line
                key={cliente.id}
                className="cursor-pointer"
                onClick={() => history.push(`/clientes/resumo/${cliente.id}`)}
              >
                <Table.Item title={cliente.name}>{cliente.name}</Table.Item>
                <Table.Item>{cliente.cpf ? formatCpf(cliente.cpf) : '-'}</Table.Item>
                <Table.Item>{cliente.telephone || '-'}</Table.Item>
                <Table.Item className="text-right">{cliente.petsCount ?? 0}</Table.Item>
                <Table.Item className="text-right">
                  {cliente.averageTicket ? `R$ ${formatMoney(cliente.averageTicket)}` : '-'}
                </Table.Item>
                <Table.Item>
                  <AbsenceBadge days={cliente.absenceDays} />
                </Table.Item>
                <Table.Item title={cliente.email}>{cliente.email || '-'}</Table.Item>
              </Table.Line>
            ))}
          </Table.Content>
        </Table.Root>

        {!carregando && !listaOrdenada.length && (
          <p className="py-6 text-center text-sm text-gray-500">
            {aviso || 'Busque por CPF, telefone, nome do cliente ou nome do pet.'}
          </p>
        )}
      </motion.div>
    </BasePage>
  )
}

export default Clientes
